import { portalText, usePortalTranslation, portalLocale } from "../../i18n/portal.js";
/**
 * The dashboard's Campaigns card — WAPP-DASH (owner, 2026-10-28: "on the customer portal running campaigns or close to
 * start or close from end should have a place in the dashboard").
 *
 * DECIDED DEFAULTS (recorded here, as the dashboard records its layout decisions):
 *
 *   - WHO SEES IT. Only a person who may read campaigns (`campaign.read` in `shell/member-capability.ts`, the route's
 *     own CAMPAIGN_READ_ACCESS: every Q10 role, viewers included, and Campaign manager), and only while at least one
 *     campaign qualifies. Otherwise there is no card at all — no empty state.
 *   - WHERE THE CAMPAIGNS COME FROM. There is no workspace-wide campaign list in the API: `GET /v1/campaigns` only
 *     creates, and campaigns are read per agent (`GET /v1/agents/{voice_agent_id}/campaigns`, `listCampaigns`). The card
 *     reads that list for every agent that has a Campaigns tab — a survey agent_template (`isSurveyAgentTemplate`, the
 *     tab strip's own rule) — and names the agent from the dashboard's agent list. No API change.
 *   - WHAT QUALIFIES. (a) running or paused now; (b) starting soon: `scheduled` with `starts_at` no more than 24 hours
 *     ahead (a start already passed that has not begun dialling still counts — it is due); (c) ending soon: running with
 *     `estimated_finish_at` no more than 24 hours ahead, shown once, as a running row with an "Ends …" note. The
 *     campaign summary carries no end date (`end_local_date` is on the detail only), so "ending soon" is the measured
 *     estimate alone.
 *   - THE ROW. The campaign's name (to its Campaign screen), its agent, a state line — "Calling now", "Outside calling
 *     hours", the Campaign screen's own words for not dialling and "Paused" (`etaText`, `NOT_DIALLING_TEXT`), "Starts
 *     today 12:00" (`startsTextOf`) and "Ends tomorrow 14:00" (`endsTextOf`), both through `formatWhen` as the slice's
 *     `context.formatWhen` — and "Called X of Y" with a slim bar.
 *   - HOW MANY, IN WHAT ORDER. At most five: running first, then ending soon (soonest end first), then paused, then
 *     starting soon (soonest start first); "See all campaigns" opens the one agent's Campaigns tab, or the agents list
 *     when several agents run campaigns.
 *   - HOW FRESH. Read again on the cadence the dashboard already reads its live figures (`LIVE_ACTIVITY_REFETCH_MS`);
 *     no poll of its own.
 */
import { useQueries } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { CSSProperties, ReactNode } from "react";

import {
  campaignCardRowsOf,
  type CampaignCardRowOf,
  type VoiceAgentCampaignsOf,
} from "@wetalk/view-model/campaign-card-group";

import { formatWhen } from "../../api/conversation-view.js";
import type { VoiceAgentView } from "../../api/gateway.js";
import { useGateway } from "../../api/provider.js";
import { LIVE_ACTIVITY_REFETCH_MS, campaignListQuery } from "../../api/query.js";
import { NOT_DIALLING_TEXT, endsTextOf, startsTextOf, type CampaignListRowView } from "../../api/slice/campaign.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { isSurveyAgentTemplate } from "../agents/[id]/agent-tabs.js";

/**
 * What qualifies, why and in what order moved to `@wetalk/view-model/campaign-card-group` (plan-034 VIEW-PKG), shared
 * with the wetalk app; re-exported here so no portal importer changes. The words and the markup stay here.
 */
export {
  CAMPAIGN_CARD_ROW_LIMIT,
  CAMPAIGN_SOON_MS,
  campaignCardGroupOf,
  campaignCardRowsOf,
  type CampaignCardGroup,
} from "@wetalk/view-model/campaign-card-group";

/** One agent's campaigns, as the card reads them. */
export type VoiceAgentCampaigns = VoiceAgentCampaignsOf<CampaignListRowView>;

export type CampaignCardRow = CampaignCardRowOf<CampaignListRowView>;

/** The row's state line, in the Campaign screen's words wherever it has them. */
export function campaignStateTextOf(row: CampaignCardRow, now: Date): string {
  const one = row.campaign;
  if (row.group === "starting_soon" && one.startsAt !== null) return startsTextOf(one.startsAt, now, formatWhen);
  if (row.group === "paused") return one.etaText;
  switch (one.diallingState) {
    case "dialling":
      return portalText("core", "dashboard.campaign_calling_now");
    case "outside_window":
      return portalText("core", "dashboard.campaign_outside_calling_hours");
    case "no_route":
    case "switched_off":
      return NOT_DIALLING_TEXT[one.diallingState];
    case "paused":
    case "finished":
      return one.etaText;
  }
}

function countText(value: number): string {
  return value.toLocaleString(portalLocale() === "el" ? "el-GR" : "en-GB");
}

function fillStyle(percent: number): CSSProperties {
  return { "--c-meter-fill": `${String(Math.max(0, Math.min(100, percent)))}%` } as CSSProperties;
}

function CampaignCardItem({ row, now }: { readonly row: CampaignCardRow; readonly now: Date }): ReactNode {
  usePortalTranslation("core");
  const one = row.campaign;
  const percent = one.personCount === 0 ? 0 : Math.round((one.calledCount / one.personCount) * 100);
  const endsText = row.group === "ending_soon" && one.estimatedFinishAt !== null ? endsTextOf(one.estimatedFinishAt, now, formatWhen) : null;
  const dialling = row.group !== "starting_soon" && row.group !== "paused" && one.diallingState === "dialling";
  return (
    <li className="c-dash-campaign" data-group={row.group}>
      <span className="c-dash-campaign-head">
        <Link to="/campaigns/$campaignId" params={{ campaignId: one.campaignId }} className="c-dash-campaign-name">
          {one.name}
        </Link>
        <span className="c-dash-campaign-agent">{row.voiceAgentName}</span>
      </span>
      <span className="c-dash-campaign-state">
        {dialling ? <span className="c-dash-pulse" aria-hidden="true" /> : null}
        <span>{campaignStateTextOf(row, now)}</span>
        {endsText === null ? null : <span className="c-dash-campaign-ends">{endsText}</span>}
      </span>
      <span className="c-dash-campaign-progress">
        <span className="c-dash-campaign-count">
          {portalText("core", "dashboard.campaign_called_of", { value0: countText(one.calledCount), value1: countText(one.personCount) })}
        </span>
        <span
          className="c-dash-campaign-track"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-label={portalText("core", "dashboard.campaign_called_percent", { value0: one.name, value1: String(percent) })}
        >
          <span className="c-dash-campaign-fill" style={fillStyle(percent)} />
        </span>
      </span>
    </li>
  );
}

/**
 * The card, or nothing: drawn only for a person who may read campaigns and while one qualifies. `voiceAgent` is the
 * dashboard's own agent list (already read on the live cadence).
 */
export function CampaignCard({ voiceAgent }: { readonly voiceAgent: readonly VoiceAgentView[] }): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const mayRead = useMemberCan().can("campaign.read");
  const owner = voiceAgent.filter((one) => isSurveyAgentTemplate(one.agentTemplateCode));
  const owned = useQueries({
    queries: owner.map((one) => ({
      ...campaignListQuery(gateway, one.voiceAgentId),
      enabled: mayRead,
      refetchInterval: LIVE_ACTIVITY_REFETCH_MS,
    })),
    combine: (found) =>
      owner.map((one, index): VoiceAgentCampaigns => ({
        voiceAgentId: one.voiceAgentId,
        voiceAgentName: one.displayName,
        campaign: found[index]?.data ?? [],
      })),
  });
  if (!mayRead) return null;
  const now = new Date();
  const rows = campaignCardRowsOf(owned, now);
  if (rows.length === 0) return null;
  const runner = owned.filter((one) => one.campaign.length > 0);
  const onlyVoiceAgentId = runner.length === 1 ? (runner[0]?.voiceAgentId ?? null) : null;
  return (
    <section className="c-dash-campaigns" aria-label={portalText("core", "dashboard.campaigns_caption")}>
      <div className="c-dash-section-head">
        <p className="c-dash-label">{portalText("core", "dashboard.campaigns_title")}</p>
        {onlyVoiceAgentId === null ? (
          <Link to="/agents" search={{ status: "all", q: "" }} className="c-dash-more">
            {portalText("core", "dashboard.campaigns_see_all")}
          </Link>
        ) : (
          <Link to="/agents/$voiceAgentId" params={{ voiceAgentId: onlyVoiceAgentId }} search={{ tab: "outbound" }} className="c-dash-more">
            {portalText("core", "dashboard.campaigns_see_all")}
          </Link>
        )}
      </div>
      <ul className="c-dash-campaign-card">
        {rows.map((row) => (
          <CampaignCardItem key={row.campaign.campaignId} row={row} now={now} />
        ))}
      </ul>
    </section>
  );
}
